/**
 * One thing the reader can do, with the value that belongs to it inside the
 * control.
 *
 * Two decisions are load-bearing.
 *
 * The first is that an action with a destination is an anchor. `role="button"`
 * gives no middle-click, no open-in-new-tab, no copy-link-address and no
 * status-bar preview, and almost every action on this surface is a navigation
 * to a vendor URL — so `href` goes to `Cbutton`, which now renders a real `<a>`
 * root, and the raw `<a>` this replaces is retired.
 *
 * The second is the radius. A non-inline `Cbutton` renders a semantic root
 * wrapping a `Ccontainer`, and the `Ccontainer` is the surface that paints the
 * corner — so a `borderRadius` set on the root does nothing to the visible
 * clip. `Cbutton`'s normalised default is 40px, which is how a 10px media frame
 * once resolved to a 42.67px pill. Every kit control passes the numeric ladder
 * rung to `radius` instead, and never sets `borderRadius` inside a `Cbutton`.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import { IconArrowUpRight } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import {
  CRC_ACCENT,
  CRC_ON_ACCENT,
  CRC_R_PILL,
  CRC_TEXT,
  CRC_TOUCH_TARGET,
} from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import { CresultcardActionTone } from "./cresultcard.types";

const ICON_STROKE = 1.4;
const COMPACT_ICON_SIZE = 11;
const REGULAR_ICON_SIZE = 12;
const COMPACT_WELL = 18;
const REGULAR_WELL = 21;
/** An icon-only control is its own target, so the well becomes the whole box. */
const COMPACT_ICON_TARGET = 26;
const REGULAR_ICON_TARGET = 32;
/** Asymmetric: the icon well sits tight to the trailing edge, the label does not. */
const COMPACT_PADDING = "5px 5px 5px 11px";
const REGULAR_PADDING = "6px 6px 6px 14px";
/** `data-crc-control` is what tells the quiet click zone to keep its hands off. */
const ACTION_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/Action/Root",
  "data-crc-control": true,
};
/** The visible text is `label` then `value`; the name has to read the same. */
const NAME_SEPARATOR = " ";
const PRESET_BY_TONE: Record<CresultcardActionTone, ButtonPreset> = {
  [CresultcardActionTone.Primary]: ButtonPreset.Primary,
  [CresultcardActionTone.Secondary]: ButtonPreset.Secondary,
  [CresultcardActionTone.Quiet]: ButtonPreset.Ghost,
};

/** The fill goes on the container, never as a style override on the root. */
const CONTAINER_STYLE_BY_TONE: Record<CresultcardActionTone, CSSProperties> = {
  [CresultcardActionTone.Primary]: {
    color: CRC_ON_ACCENT,
    backgroundColor: CRC_ACCENT,
    boxShadow: "0 8px 22px -10px rgba(102,254,193,0.75)",
  },
  [CresultcardActionTone.Secondary]: {
    color: CRC_TEXT,
    backgroundColor: "rgba(255,255,255,0.07)",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
  },
  [CresultcardActionTone.Quiet]: {
    color: "rgba(244,253,250,0.66)",
    backgroundColor: "transparent",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
  },
};

const WELL_TINT_BY_TONE: Record<CresultcardActionTone, string> = {
  [CresultcardActionTone.Primary]: "rgba(0,0,0,0.14)",
  [CresultcardActionTone.Secondary]: "rgba(255,255,255,0.09)",
  [CresultcardActionTone.Quiet]: "rgba(255,255,255,0.07)",
};

interface CresultcardActionProps {
  readonly label: string;
  /** Exactly one of these. A destination is an anchor; a behaviour is a press. */
  readonly href?: string;
  readonly onPress?: () => void;
  readonly tone: CresultcardActionTone;
  readonly icon?: TablerIcon;
  /** A price, a duration, a phone number — rendered INSIDE the control. */
  readonly value?: string;
  readonly compact?: boolean;
  readonly fullWidth?: boolean;
  /**
   * The glyph alone, in a circular target. `label` still carries the
   * accessible name — this hides the word, never the meaning — and a value has
   * nowhere to go, so an icon-only action may not declare one.
   */
  readonly iconOnly?: boolean;
  /** Set when the control sits inside another interactive surface. */
  readonly composite?: boolean;
  /**
   * Trailing room, in pixels, for a control that is layered over this one's own
   * trailing edge — today that is exactly one thing: the action group's chevron
   * at full width. A chevron cannot be nested INSIDE the control (interactive
   * content inside an anchor is invalid, and §7.1 owns the split), so it is
   * layered over a strip this control gives up. Without the reserve the chevron
   * lands on the icon well and two glyphs share one 21px circle.
   */
  readonly trailingReserve?: number;
}

export function CresultcardAction({
  label,
  href,
  onPress,
  tone,
  icon: Glyph = IconArrowUpRight,
  value,
  compact = false,
  fullWidth = false,
  iconOnly = false,
  composite = false,
  trailingReserve,
}: CresultcardActionProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const wellSize = compact ? COMPACT_WELL : REGULAR_WELL;
  const iconSize = compact ? COMPACT_ICON_SIZE : REGULAR_ICON_SIZE;
  const iconOnlySize = Math.max(
    compact ? COMPACT_ICON_TARGET : REGULAR_ICON_TARGET,
    isMobile ? CRC_TOUCH_TARGET : 0,
  );
  // An icon-only control hides the word and keeps the meaning, so `label` alone
  // is its name. Everywhere else the value is rendered INSIDE the control, and
  // a control reading `Call (212) 989-6131` whose name is `Call` is a
  // label-in-name mismatch: a reader saying "click Call two one two" is asking
  // for something no name matches (WCAG 2.5.3).
  const accessibleName =
    iconOnly || value == null ? label : `${label}${NAME_SEPARATOR}${value}`;

  return (
    <Cbutton
      ariaLabel={accessibleName}
      composite={composite}
      containerStyle={{
        ...CONTAINER_STYLE_BY_TONE[tone],
        // The floor is the kit's, not the pillar's: a 28px target at Mobile is
        // a control a thumb misses.
        minHeight: iconOnly ? iconOnlySize : isMobile ? CRC_TOUCH_TARGET : undefined,
        minWidth: iconOnly ? iconOnlySize : undefined,
        width: fullWidth ? "100%" : undefined,
        justifyContent: fullWidth || iconOnly ? "center" : undefined,
        paddingInlineEnd: trailingReserve,
      }}
      contentStyle={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: iconOnly ? "center" : undefined,
        gap: iconOnly ? 0 : compact ? 6 : 8,
        fontSize: compact ? 10.5 : 11.5,
        fontWeight: 700,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
      }}
      href={href}
      onClick={onPress}
      padding={iconOnly ? 0 : compact ? COMPACT_PADDING : REGULAR_PADDING}
      preset={PRESET_BY_TONE[tone]}
      radius={CRC_R_PILL}
      rootAttributes={{
        ...ACTION_ROOT_ATTRIBUTES,
      }}
      style={{ width: fullWidth ? "100%" : undefined }}
    >
      {!iconOnly && label}
      {!iconOnly && value != null && (
        <span
          style={{ opacity: 0.72, fontWeight: 600 }}
        >
          {value}
        </span>
      )}
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: iconOnly ? iconOnlySize : wellSize,
          height: iconOnly ? iconOnlySize : wellSize,
          flexShrink: 0,
          borderRadius: "50%",
          backgroundColor: iconOnly
            ? "transparent"
            : WELL_TINT_BY_TONE[tone],
        }}
      >
        <Glyph size={iconSize} stroke={ICON_STROKE} />
      </span>
    </Cbutton>
  );
}
